import { CartLinesUpdateEvent } from '@shopify/events';

class ArthurUpsellCarousel extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-upsell-list]');
    this.cards = [...this.querySelectorAll('.af-cart-upsells__item')];
    this.previous = this.querySelector('[data-upsell-previous]');
    this.next = this.querySelector('[data-upsell-next]');
    if (!this.list || !this.previous || !this.next || this.cards.length < 2) return;
    this.onClick = (event) => {
      const direction = event.target.closest('[data-upsell-previous]') ? -1 : event.target.closest('[data-upsell-next]') ? 1 : 0;
      if (!direction) return;
      const current = this.cards.reduce((best, card, index) =>
        Math.abs(card.offsetLeft - this.cards[0].offsetLeft - this.list.scrollLeft) <
        Math.abs(this.cards[best].offsetLeft - this.cards[0].offsetLeft - this.list.scrollLeft) ? index : best, 0);
      const target = this.cards[Math.max(0, Math.min(this.cards.length - 1, current + direction))];
      this.list.scrollTo({ left: target.offsetLeft - this.cards[0].offsetLeft, behavior: 'smooth' });
    };
    this.updateArrows = () => {
      this.previous.disabled = this.list.scrollLeft <= 2;
      this.next.disabled = this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 2;
    };
    this.addEventListener('click', this.onClick);
    this.list.addEventListener('scroll', this.updateArrows, { passive: true });
    this.resizeObserver = new ResizeObserver(this.updateArrows);
    this.resizeObserver.observe(this.list);
    this.updateArrows();
  }

  disconnectedCallback() {
    if (this.onClick) this.removeEventListener('click', this.onClick);
    if (this.updateArrows) this.list?.removeEventListener('scroll', this.updateArrows);
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('af-upsell-carousel')) customElements.define('af-upsell-carousel', ArthurUpsellCarousel);

// One handler covers the product form and recommendations re-rendered inside the cart drawer.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-af-ajax-cart]')) return;
  event.preventDefault();

  const button = form.querySelector('[type="submit"]');
  const stickyButton = event.submitter?.matches('[data-sticky-submit]') ? event.submitter : document.querySelector(`[data-sticky-submit][form="${form.id}"]`);
  const status = form.querySelector('[role="status"]') || form.querySelector('.ao-product__message');
  const data = new FormData(form);
  const variantId = data.get('id');
  if (!variantId || button?.disabled || form.dataset.afAdding) return;
  data.set('sections', 'cart-drawer-section');
  data.set('sections_url', window.location.pathname);

  if (button) button.disabled = true;
  if (stickyButton) stickyButton.disabled = true;
  form.dataset.afAdding = 'true';
  if (status) status.textContent = '';

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(variantId), quantity: Number(data.get('quantity')) || 1 }],
    promise: deferred.promise,
  }));

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST', body: data, credentials: 'same-origin', headers: { Accept: 'application/json' },
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Could not add this item.');
    const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
    if (!cartResponse.ok) throw new Error('Could not refresh the cart.');
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: result.sections || {}, items: cart.items, itemCount: cart.item_count, didError: false, source: 'arthur-cart-add' },
    });
    if (status) status.textContent = 'Added to bag';
  } catch (error) {
    // A failed refresh after a successful add may still leave the item in the cart.
    deferred.resolve({ detail: { didError: true } });
    if (status) status.textContent = error.message || 'Could not update your bag. Please try again.';
  } finally {
    if (button) button.disabled = button.textContent.trim() === 'Sold out';
    if (stickyButton) stickyButton.disabled = stickyButton.textContent.trim() === 'Sold out';
    delete form.dataset.afAdding;
  }
});
